import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  published: boolean;
  featured: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  read_time: number;
  tags: string[];
  seo_title: string;
  seo_description: string;
  image_url: string | null;
}

export function useBlogs() {
  const queryClient = useQueryClient();

  // Get all published blog posts
  const { data: blogs = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['blogs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false });
      
      if (error) throw error;
      return data as BlogPost[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get featured blog posts
  const { data: featuredBlogs = [] } = useQuery({
    queryKey: ['featured-blogs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .eq('published', true)
        .eq('featured', true)
        .order('published_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data as BlogPost[];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Get blog post by slug
  const getBlogBySlug = (slug: string) => {
    return useQuery({
      queryKey: ['blog', slug],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('blogs')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single();
        
        if (error) throw error;
        return data as BlogPost;
      },
      enabled: !!slug,
    });
  };

  // Admin: Get all blog posts (including unpublished)
  const getAllBlogs = (isAdmin: boolean) => {
    return useQuery({
      queryKey: ['all-blogs', isAdmin],
      queryFn: async () => {
        let query = supabase.from('blogs').select('*');
        
        if (!isAdmin) {
          query = query.eq('published', true);
        }
        
        query = query.order('created_at', { ascending: false });
        
        const { data, error } = await query;
        
        if (error) throw error;
        return data as BlogPost[];
      },
      enabled: isAdmin,
    });
  };

  // Create blog post
  const createBlog = useMutation({
    mutationFn: async (blogData: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('blogs')
        .insert([{
          ...blogData,
          slug: blogData.slug || blogData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''),
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['all-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blogs'] });
    },
  });

  // Update blog post
  const updateBlog = useMutation({
    mutationFn: async ({ id, ...blogData }: Partial<BlogPost> & { id: string }) => {
      const { data, error } = await supabase
        .from('blogs')
        .update(blogData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['all-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['blog'] }); // Invalidate all blog queries
    },
  });

  // Delete blog post
  const deleteBlog = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['all-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blogs'] });
    },
  });

  // Publish/unpublish blog post
  const togglePublish = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const { data, error } = await supabase
        .from('blogs')
        .update({ published, published_at: published ? new Date().toISOString() : null })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blogs'] });
      queryClient.invalidateQueries({ queryKey: ['all-blogs'] });
      queryClient.invalidateQueries({ queryKey: ['featured-blogs'] });
    },
  });

  return {
    blogs,
    featuredBlogs,
    isLoading,
    isError,
    refetch,
    getBlogBySlug,
    getAllBlogs,
    createBlog,
    updateBlog,
    deleteBlog,
    togglePublish,
  };
}